/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRentalItemById } from "../../../../services/rentalService";
import RentalWeekdayPricing from "./RentalWeekdayPricing";
import AdminChargeSelect from "../../../common/AdminChargeSelect";
import {
    isValidAdminCharge,
    resolveAdminChargeFormFields,
    DEFAULT_ADMIN_CHARGE,
    DEFAULT_ADMIN_CHARGE_TYPE,
} from "../../../../constants/adminCharge";
import {
    getCategoryPricingBasis,
    isRentalPricingFieldVisible,
    isRentalPricingFieldRequired,
    RENTAL_PRICING_BASIS,
    validateRentalPricing,
} from "../../../../config/rentalCategoryFormConfig";
import {
    CollapsibleBlock,
    PriceTable,
    INP,
} from "../../activity/pricing/pricingCompactUi";

const RentalPricing = ({
    setData,
    formDataRef,
    rentalId,
    validationErrors,
    onValidityChange,
    compact = false,
}) => {
    const [formData, setFormData] = useState({
        price_per_hour: "",
        price_per_day: "",
        security_deposit: "",
        advance_type: "",
        advance_value: "",
        discount_type: "",
        discount_value: "",
        admin_charge_type: DEFAULT_ADMIN_CHARGE_TYPE,
        admin_charge_value: String(DEFAULT_ADMIN_CHARGE),
    });

    const validate = (data, basis) => {
        const errors = validateRentalPricing(data, basis);
        if (data.admin_charge_type === "percent" && !isValidAdminCharge(data.admin_charge_value)) {
            errors.admin_charge_value = "Select admin charge: 5%, 10%, 15%, or 20%.";
        }
        return errors;
    };

    const { data: rentalData, isLoading } = useQuery({
        queryKey: ["rentalItem", rentalId],
        queryFn: () => getRentalItemById(rentalId),
        enabled: !!rentalId,
        staleTime: 120_000,
    });

    const pricingBasis = useMemo(
        () => getCategoryPricingBasis(rentalData?.data?.category),
        [rentalData?.data?.category]
    );

    const showHourRate = isRentalPricingFieldVisible(pricingBasis, "price_per_hour");
    const showDayRate = isRentalPricingFieldVisible(pricingBasis, "price_per_day");
    const dayRateRequired = isRentalPricingFieldRequired(pricingBasis, "price_per_day");
    const hourRateRequired = isRentalPricingFieldRequired(pricingBasis, "price_per_hour");

    useEffect(() => {
        if (formDataRef) {
            formDataRef.current = formData;
        }
        setData?.(formData);
    }, [formData, setData, formDataRef]);

    useEffect(() => {
        const errs = validate(formData, pricingBasis);
        onValidityChange?.(Object.keys(errs).length === 0);
    }, [formData, pricingBasis, onValidityChange]);

    const lastHydratedRentalIdRef = useRef(null);

    useEffect(() => {
        if (!rentalData?.data || !rentalId) return;

        if (lastHydratedRentalIdRef.current === rentalId) return;
        lastHydratedRentalIdRef.current = rentalId;

        const pricing = rentalData.data.pricing_rule || {};
        const dt = pricing.discount_type;
        const advt = pricing.advance_type;
        const adminCharge = resolveAdminChargeFormFields(pricing);
        setFormData({
            price_per_hour: pricing.price_per_hour ?? rentalData.data.price_per_hour ?? "",
            price_per_day: pricing.price_per_day ?? rentalData.data.price_per_day ?? "",
            security_deposit: pricing.security_deposit ?? rentalData.data.security_deposit ?? "",
            advance_type: advt === "flat" || advt === "percent" ? advt : "",
            advance_value:
                pricing.advance_value != null && pricing.advance_value !== ""
                    ? String(pricing.advance_value)
                    : "",
            discount_type: dt === "flat" || dt === "percent" ? dt : "",
            discount_value:
                pricing.discount_value != null && pricing.discount_value !== ""
                    ? String(pricing.discount_value)
                    : "",
            ...adminCharge,
        });
    }, [rentalId, rentalData?.data?.id]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const [advanceOpen, setAdvanceOpen] = useState(false);
    const [discountOpen, setDiscountOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);

    const inputClasses = (error) =>
        `w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:border-primary text-sm`;

    if (isLoading) return <div className="p-8 text-center text-gray-500">Loading pricing details...</div>;

    const discountErr = validationErrors?.discount_value;
    const discountOn = formData.discount_type === "flat" || formData.discount_type === "percent";
    const adminChargeErr = validationErrors?.admin_charge_value;
    const adminChargeOn = formData.admin_charge_type === "flat" || formData.admin_charge_type === "percent";
    const advanceErr = validationErrors?.advance_value;
    const advanceOn = formData.advance_type === "flat" || formData.advance_type === "percent";

    const typeRadioRow = (name, typeField, valueField, isOn, err) => (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
                {[
                    { label: "None", type: "" },
                    { label: "Flat (₹)", type: "flat" },
                    { label: "Percent (%)", type: "percent" },
                ].map(({ label, type }) => (
                    <label
                        key={type || "none"}
                        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs text-gray-700"
                    >
                        <input
                            type="radio"
                            name={name}
                            checked={type ? formData[typeField] === type : !isOn}
                            onChange={() => {
                                handleChange(typeField, type);
                                if (!type) handleChange(valueField, "");
                            }}
                            className="text-primary"
                        />
                        {label}
                    </label>
                ))}
            </div>
            <div className="relative w-full sm:w-44 sm:shrink-0">
                {formData[typeField] === "flat" && (
                    <span className="absolute left-2 top-1/2 z-[1] -translate-y-1/2 text-gray-400 text-xs">₹</span>
                )}
                {name === "admin_charge_type" && formData.admin_charge_type === "percent" ? (
                    <AdminChargeSelect
                        value={formData.admin_charge_value ?? ""}
                        onChange={(value) => handleChange("admin_charge_value", value)}
                        disabled={!adminChargeOn}
                        error={Boolean(err)}
                    />
                ) : (
                    <input
                        type="number"
                        disabled={!isOn}
                        value={formData[valueField]}
                        onChange={(e) => handleChange(valueField, e.target.value)}
                        className={`${INP} ${formData[typeField] === "flat" ? "pl-6" : ""} ${formData[typeField] === "percent" ? "pr-8" : ""} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500`}
                        placeholder={formData[typeField] === "percent" ? "e.g. 50" : "0.00"}
                        min="0"
                        max={formData[typeField] === "percent" ? "100" : undefined}
                        step={formData[typeField] === "percent" ? "0.1" : "0.01"}
                    />
                )}
                {formData[typeField] === "percent" && name !== "admin_charge_type" && (
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">%</span>
                )}
            </div>
            {Array.isArray(err) ? (
                <p className="text-xs text-red-500 sm:col-span-2">{err[0]}</p>
            ) : err ? (
                <p className="text-xs text-red-500 sm:col-span-2">{err}</p>
            ) : null}
        </div>
    );

    const adminChargeRadioRow = (err) => (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
                {[
                    { label: "Flat (₹)", type: "flat" },
                    { label: "Percent (%)", type: "percent" },
                ].map(({ label, type }) => (
                    <label
                        key={type}
                        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs text-gray-700"
                    >
                        <input
                            type="radio"
                            name="admin_charge_type"
                            checked={formData.admin_charge_type === type}
                            onChange={() => {
                                handleChange("admin_charge_type", type);
                                if (type === "percent" && !isValidAdminCharge(formData.admin_charge_value)) {
                                    handleChange("admin_charge_value", String(DEFAULT_ADMIN_CHARGE));
                                }
                            }}
                            className="text-primary"
                        />
                        {label}
                    </label>
                ))}
            </div>
            <div className="relative w-full sm:w-44 sm:shrink-0">
                {formData.admin_charge_type === "flat" && (
                    <span className="absolute left-2 top-1/2 z-[1] -translate-y-1/2 text-xs text-gray-400">₹</span>
                )}
                {formData.admin_charge_type === "percent" ? (
                    <AdminChargeSelect
                        value={formData.admin_charge_value ?? ""}
                        onChange={(value) => handleChange("admin_charge_value", value)}
                        error={Boolean(err)}
                    />
                ) : (
                    <input
                        type="number"
                        value={formData.admin_charge_value}
                        onChange={(e) => handleChange("admin_charge_value", e.target.value)}
                        className={`${INP} pl-6`}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                    />
                )}
            </div>
            {Array.isArray(err) ? (
                <p className="text-xs text-red-500 sm:col-span-2">{err[0]}</p>
            ) : err ? (
                <p className="text-xs text-red-500 sm:col-span-2">{err}</p>
            ) : null}
        </div>
    );

    if (compact) {
        return (
            <div className="space-y-4">
                <PriceTable columns={["Rate type", "Amount (₹)", "Notes"]}>
                    {showDayRate ? (
                    <tr>
                        <td className="py-2 pr-2 font-medium text-gray-800">
                            Per day{dayRateRequired ? <span className="text-red-500"> *</span> : null}
                        </td>
                        <td className="py-2 pr-2">
                            <input
                                type="number"
                                value={formData.price_per_day}
                                onChange={(e) => handleChange("price_per_day", e.target.value)}
                                className={INP}
                                placeholder="0.00"
                                required={dayRateRequired}
                            />
                            {validationErrors?.price_per_day && (
                                <p className="mt-1 text-xs text-red-500">{validationErrors.price_per_day[0]}</p>
                            )}
                        </td>
                        <td className="py-2 text-xs text-gray-500">Used for bookings of 1 day or more</td>
                    </tr>
                    ) : null}
                    {showHourRate ? (
                    <tr>
                        <td className="py-2 pr-2 font-medium text-gray-800">
                            Per hour{hourRateRequired ? <span className="text-red-500"> *</span> : null}
                        </td>
                        <td className="py-2 pr-2">
                            <input
                                type="number"
                                value={formData.price_per_hour}
                                onChange={(e) => handleChange("price_per_hour", e.target.value)}
                                className={INP}
                                placeholder="0.00"
                                required={hourRateRequired}
                            />
                            {validationErrors?.price_per_hour && (
                                <p className="mt-1 text-xs text-red-500">{validationErrors.price_per_hour[0]}</p>
                            )}
                        </td>
                        <td className="py-2 text-xs text-gray-500">Used when booking is under 24 hours</td>
                    </tr>
                    ) : null}
                    <tr>
                        <td className="py-2 pr-2 font-medium text-gray-800">Security deposit</td>
                        <td className="py-2 pr-2">
                            <input
                                type="number"
                                value={formData.security_deposit}
                                onChange={(e) => handleChange("security_deposit", e.target.value)}
                                className={INP}
                                placeholder="0.00"
                            />
                        </td>
                        <td className="py-2 text-xs text-gray-500">Held during rental</td>
                    </tr>
                </PriceTable>

                <CollapsibleBlock
                    title="Advance payment"
                    subtitle={advanceOn ? `${formData.advance_type === "percent" ? formData.advance_value + "%" : "₹" + formData.advance_value}` : "None"}
                    open={advanceOpen}
                    onToggle={() => setAdvanceOpen((o) => !o)}
                >
                    {typeRadioRow("advance_type", "advance_type", "advance_value", advanceOn, advanceErr)}
                </CollapsibleBlock>

                <CollapsibleBlock
                    title="Discount"
                    subtitle={discountOn ? `${formData.discount_type === "percent" ? formData.discount_value + "%" : "₹" + formData.discount_value}` : "None"}
                    open={discountOpen}
                    onToggle={() => setDiscountOpen((o) => !o)}
                >
                    {typeRadioRow("discount_type", "discount_type", "discount_value", discountOn, discountErr)}
                </CollapsibleBlock>

                <CollapsibleBlock
                    title="Admin charge *"
                    subtitle={`${formData.admin_charge_type === "percent" ? formData.admin_charge_value + "%" : "₹" + formData.admin_charge_value}`}
                    open={adminOpen}
                    onToggle={() => setAdminOpen((o) => !o)}
                >
                    {adminChargeRadioRow(adminChargeErr)}
                </CollapsibleBlock>

                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[11px] text-gray-600">
                        <span className="font-semibold text-gray-700">GST:</span> 18% is applied on rent subtotal at checkout.
                    </p>
                </div>

                {rentalId && showHourRate && Number(formData.price_per_hour) > 0 ? (
                    <RentalWeekdayPricing rentalId={rentalId} regularPricePerHour={formData.price_per_hour} />
                ) : null}

                <div className="flex gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <i className="fi fi-rr-info mt-0.5 text-blue-500" />
                    <p className="text-xs leading-relaxed text-blue-700">
                        {pricingBasis === RENTAL_PRICING_BASIS.HYBRID ? (
                            <>
                                For all rentals, enter both rates: <strong>per hour</strong> is used for bookings under 24 hours,
                                and <strong>per day</strong> is used for bookings of 1 day or more.
                            </>
                        ) : (
                            <>
                                For all rentals, enter both rates: <strong>per hour</strong> is used for bookings under 24 hours,
                                and <strong>per day</strong> is used for bookings of 1 day or more.
                            </>
                        )}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-base font-semibold leading-none">
                        ₹
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 leading-tight">Pricing & Deposit</h2>
                        <p className="text-xs text-gray-500">Set rates and deposit</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {showDayRate ? (
                    <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-gray-700">
                            Price / day{dayRateRequired ? <span className="text-red-500"> *</span> : null}
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                            <input
                                type="number"
                                value={formData.price_per_day}
                                onChange={(e) => handleChange("price_per_day", e.target.value)}
                                className={`${inputClasses(validationErrors?.price_per_day)} pl-8`}
                                placeholder="0.00"
                                required={dayRateRequired}
                            />
                        </div>
                        {validationErrors?.price_per_day && (
                            <p className="text-red-500 text-xs">{validationErrors.price_per_day[0]}</p>
                        )}
                    </div>
                    ) : null}

                    {showHourRate ? (
                    <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-gray-700">
                            Price / hour{hourRateRequired ? <span className="text-red-500"> *</span> : null}
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                            <input
                                type="number"
                                value={formData.price_per_hour}
                                onChange={(e) => handleChange("price_per_hour", e.target.value)}
                                className={`${inputClasses(validationErrors?.price_per_hour)} pl-8`}
                                placeholder="0.00"
                                required={hourRateRequired}
                            />
                        </div>
                        {validationErrors?.price_per_hour && (
                            <p className="text-red-500 text-xs">{validationErrors.price_per_hour[0]}</p>
                        )}
                    </div>
                    ) : null}

                    <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-gray-700">Security deposit</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                            <input
                                type="number"
                                value={formData.security_deposit}
                                onChange={(e) => handleChange("security_deposit", e.target.value)}
                                className={`${inputClasses(false)} pl-8`}
                                placeholder="0.00"
                            />
                        </div>
                        <p className="text-[10px] text-gray-400">Held during rental period</p>
                    </div>

                    <div className="space-y-1.5 md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700">Advance</label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex flex-wrap gap-x-4 gap-y-2 sm:flex-1 sm:min-w-0">
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="advance_type"
                                        checked={!advanceOn}
                                        onChange={() => {
                                            handleChange("advance_type", "");
                                            handleChange("advance_value", "");
                                        }}
                                        className="text-primary"
                                    />
                                    None
                                </label>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="advance_type"
                                        checked={formData.advance_type === "flat"}
                                        onChange={() => handleChange("advance_type", "flat")}
                                        className="text-primary"
                                    />
                                    Flat (₹)
                                </label>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="advance_type"
                                        checked={formData.advance_type === "percent"}
                                        onChange={() => handleChange("advance_type", "percent")}
                                        className="text-primary"
                                    />
                                    Percent (%)
                                </label>
                            </div>
                            <div className="relative w-full sm:w-44 sm:shrink-0">
                                {formData.advance_type === "flat" && (
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-[1]">₹</span>
                                )}
                                <input
                                    type="number"
                                    disabled={!advanceOn}
                                    value={formData.advance_value}
                                    onChange={(e) => handleChange("advance_value", e.target.value)}
                                    className={`${inputClasses(advanceErr)} w-full ${formData.advance_type === "flat" ? "pl-8" : ""} ${formData.advance_type === "percent" ? "pr-10" : ""} disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed`}
                                    placeholder={formData.advance_type === "percent" ? "e.g. 50" : "0.00"}
                                    min="0"
                                    max={formData.advance_type === "percent" ? "100" : undefined}
                                    step={formData.advance_type === "percent" ? "0.1" : "0.01"}
                                />
                                {formData.advance_type === "percent" && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
                                        %
                                    </span>
                                )}
                            </div>
                        </div>
                        {Array.isArray(advanceErr) ? (
                            <p className="text-red-500 text-xs">{advanceErr[0]}</p>
                        ) : advanceErr ? (
                            <p className="text-red-500 text-xs">{advanceErr}</p>
                        ) : (
                            <p className="text-[10px] text-gray-400">Example: 50% = customer pays 50% now</p>
                        )}
                    </div>

                    <div className="space-y-1.5 md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700">Discount</label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex flex-wrap gap-x-4 gap-y-2 sm:flex-1 sm:min-w-0">
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="discount_type"
                                        checked={!discountOn}
                                        onChange={() => {
                                            handleChange("discount_type", "");
                                            handleChange("discount_value", "");
                                        }}
                                        className="text-primary"
                                    />
                                    None
                                </label>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="discount_type"
                                        checked={formData.discount_type === "flat"}
                                        onChange={() => handleChange("discount_type", "flat")}
                                        className="text-primary"
                                    />
                                    Flat (₹)
                                </label>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="discount_type"
                                        checked={formData.discount_type === "percent"}
                                        onChange={() => handleChange("discount_type", "percent")}
                                        className="text-primary"
                                    />
                                    Percent (%)
                                </label>
                            </div>
                            <div className="relative w-full sm:w-44 sm:shrink-0">
                                {formData.discount_type === "flat" && (
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-[1]">₹</span>
                                )}
                                <input
                                    type="number"
                                    disabled={!discountOn}
                                    value={formData.discount_value}
                                    onChange={(e) => handleChange("discount_value", e.target.value)}
                                    className={`${inputClasses(discountErr)} w-full ${formData.discount_type === "flat" ? "pl-8" : ""} ${formData.discount_type === "percent" ? "pr-10" : ""} disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed`}
                                    placeholder={formData.discount_type === "percent" ? "e.g. 10" : "0.00"}
                                    min="0"
                                    max={formData.discount_type === "percent" ? "100" : undefined}
                                    step={formData.discount_type === "percent" ? "0.1" : "0.01"}
                                />
                                {formData.discount_type === "percent" && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
                                        %
                                    </span>
                                )}
                            </div>
                        </div>
                        {Array.isArray(discountErr) ? (
                            <p className="text-red-500 text-xs">{discountErr[0]}</p>
                        ) : discountErr ? (
                            <p className="text-red-500 text-xs">{discountErr}</p>
                        ) : null}
                    </div>

                    <div className="space-y-1.5 md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700">
                            Admin charge <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex flex-wrap gap-x-4 gap-y-2 sm:flex-1 sm:min-w-0">
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="admin_charge_type"
                                        checked={formData.admin_charge_type === "flat"}
                                        onChange={() => handleChange("admin_charge_type", "flat")}
                                        className="text-primary"
                                    />
                                    Flat (₹)
                                </label>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                                    <input
                                        type="radio"
                                        name="admin_charge_type"
                                        checked={formData.admin_charge_type === "percent"}
                                        onChange={() => {
                                            handleChange("admin_charge_type", "percent");
                                            if (!isValidAdminCharge(formData.admin_charge_value)) {
                                                handleChange("admin_charge_value", String(DEFAULT_ADMIN_CHARGE));
                                            }
                                        }}
                                        className="text-primary"
                                    />
                                    Percent (%)
                                </label>
                            </div>
                            <div className="relative w-full sm:w-44 sm:shrink-0">
                                {formData.admin_charge_type === "flat" && (
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-[1]">₹</span>
                                )}
                                {formData.admin_charge_type === "percent" ? (
                                    <AdminChargeSelect
                                        value={formData.admin_charge_value ?? ""}
                                        onChange={(value) =>
                                            handleChange("admin_charge_value", value)
                                        }
                                        error={Boolean(adminChargeErr)}
                                    />
                                ) : (
                                    <input
                                        type="number"
                                        value={formData.admin_charge_value}
                                        onChange={(e) => handleChange("admin_charge_value", e.target.value)}
                                        className={`${inputClasses(adminChargeErr)} w-full pl-8`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                )}
                            </div>
                        </div>
                        {Array.isArray(adminChargeErr) ? (
                            <p className="text-red-500 text-xs">{adminChargeErr[0]}</p>
                        ) : adminChargeErr ? (
                            <p className="text-red-500 text-xs">{adminChargeErr}</p>
                        ) : null}
                    </div>

                    <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] text-gray-600">
                            <span className="font-semibold text-gray-700">GST:</span> 18% is applied on rent subtotal
                            charge at checkout (fixed; not editable here).
                        </p>
                    </div>

                    <div className="md:col-span-2 pt-2">
                        <p className="text-[10px] text-gray-400">
                            Tip: Use % for advance/admin charge when you want it to scale with booking total.
                        </p>
                    </div>
                </div>
            </div>

            {rentalId && showHourRate && Number(formData.price_per_hour) > 0 ? (
                <RentalWeekdayPricing
                    rentalId={rentalId}
                    regularPricePerHour={formData.price_per_hour}
                />
            ) : null}

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex gap-3">
                <i className="fi fi-rr-info text-blue-500 mt-1"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                    {pricingBasis === RENTAL_PRICING_BASIS.HYBRID ? (
                        <>
                            For all rentals, enter both rates: <strong>per hour</strong> is used for bookings under 24 hours,
                            and <strong>per day</strong> is used for bookings of 1 day or more.
                        </>
                    ) : (
                        <>
                            For all rentals, enter both rates: <strong>per hour</strong> is used for bookings under 24 hours,
                            and <strong>per day</strong> is used for bookings of 1 day or more.
                        </>
                    )}
                </p>
            </div>
        </div>
    );
};

export default RentalPricing;
